import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Check, Globe, Loader2 } from 'lucide-react';
import i18n, { loadLanguage } from '@/i18n';
import { SUPPORTED_LANGUAGES } from '@/i18n/languages';
import { writeStoredLocale } from '@/i18n/storage';

/**
 * Einstellungen → Sprache.
 *
 * Die Sprachnamen stehen bewusst IMMER in ihrer eigenen Sprache (nativeName) —
 * so findet man zurück, wenn man versehentlich eine fremde Sprache gewählt hat.
 *
 * Nach der Auswahl wird neu geladen: das garantiert, dass wirklich JEDE Stelle
 * (auch zwischengespeicherte Listen und gespeicherte Formatierungen) die neue
 * Sprache verwendet — kein halb übersetzter Zustand.
 */
export default function LanguageSection() {
  const { t } = useTranslation('settings');
  const [pending, setPending] = useState<string | null>(null);
  const active = i18n.language;

  const change = async (code: string) => {
    if (code === active || pending) return;
    setPending(code);
    try {
      const ok = await loadLanguage(code);
      if (!ok) throw new Error('unsupported');
      if (!writeStoredLocale(code)) throw new Error('storage');
      window.location.reload();
    } catch {
      setPending(null);
      toast.error(t('language.loadFailed', 'Sprache konnte nicht geladen werden'));
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-base font-semibold text-slate-900">
          {t('language.heading', 'Anzeigesprache')}
        </h3>
        <p className="text-sm text-slate-500">
          {t('language.hint', 'Gilt nur für diese Oberfläche. E-Mails an Kunden sind davon nicht betroffen.')}
        </p>
      </div>

      <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
        {SUPPORTED_LANGUAGES.map((lang) => {
          const isActive = lang.code === active;
          const isPending = pending === lang.code;
          return (
            <button
              key={lang.code}
              onClick={() => change(lang.code)}
              disabled={!!pending}
              className={`flex items-center justify-between rounded-md border px-4 py-3 text-left transition disabled:opacity-60 ${
                isActive
                  ? 'border-[#0F99FD] bg-[#0F99FD]/5 ring-1 ring-[#0F99FD]/20'
                  : 'border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-2.5 min-w-0">
                <Globe className={`h-4 w-4 shrink-0 ${isActive ? 'text-[#0F99FD]' : 'text-slate-400'}`} />
                <span className="min-w-0">
                  {/* nativeName wird NIE übersetzt */}
                  <span className={`block truncate text-sm font-semibold ${isActive ? 'text-[#0F99FD]' : 'text-slate-700'}`}>
                    {lang.nativeName}
                  </span>
                  <span className="block truncate text-[11px] uppercase tracking-wide text-slate-400">
                    {lang.code}
                  </span>
                </span>
              </span>
              {isPending ? (
                <Loader2 className="h-4 w-4 shrink-0 animate-spin text-[#0F99FD]" />
              ) : isActive ? (
                <span className="flex shrink-0 items-center gap-1 text-[11px] font-semibold text-[#0F99FD]">
                  <Check className="h-3.5 w-3.5" /> {t('language.current', 'Aktiv')}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
